import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Box,
  Flex,
  IconButton,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import styles from '../styles/SalesReportTable.module.css';

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach(row => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <Select
      value={filterValue}
      onChange={e => {
        setFilter(e.target.value || undefined);
      }}
      size="sm"
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </Select>
  );
}

class GenericTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      pageIndex: 0,
      pageSize: this.props.initialPageSize || 10,
      sortBy: [],
      filters: {},
      columnWidths: {},
      isResizing: false,
      resizingColumn: null,
    };
    this.tableRef = React.createRef();
  }

  componentDidMount() {
    // Set default column widths if not provided
    const { columns, tbodyBorder = '1px solid black' } = this.props;
    const defaultWidths = {};
    columns.forEach(column => {
      if (!this.state.columnWidths[column.accessor]) {
        defaultWidths[column.accessor] = column.defaultWidth || 150; // Default width
      }
    });
    if (Object.keys(defaultWidths).length > 0) {
      this.setState(prevState => ({
        columnWidths: { ...prevState.columnWidths, ...defaultWidths }
      }));
    }
  }

  handleSort = (columnId) => {
    if (this.props.disableSort) return;
    this.setState(prevState => {
      const sortBy = prevState.sortBy[0]?.id === columnId
        ? [{ id: columnId, desc: !prevState.sortBy[0].desc }]
        : [{ id: columnId, desc: false }];
      return { sortBy };
    });
  }

  handleFilter = (columnId, value) => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        [columnId]: value,
      },
    }));
  }

  handleGotoPage = (page) => {
    if (this.props.manualPagination && this.props.onPageChange) {
      this.props.onPageChange(page);
    } else {
      this.setState({ pageIndex: page });
    }
  };

  handleNextPage = () => {
    if (this.props.manualPagination) {
      const { pageIndex: cpi = 0, pageSize: cps = 10, totalRowCount = 0, onPageChange } = this.props;
      const pageCount = Math.max(1, Math.ceil((Number(totalRowCount) || 0) / Math.max(1, cps)));
      if (cpi < pageCount - 1) onPageChange?.(cpi + 1);
    } else {
      this.setState(prevState => ({ pageIndex: prevState.pageIndex + 1 }));
    }
  };

  handlePreviousPage = () => {
    if (this.props.manualPagination) {
      const { pageIndex: cpi = 0, onPageChange } = this.props;
      if (cpi > 0) onPageChange?.(cpi - 1);
    } else {
      this.setState(prevState => ({ pageIndex: prevState.pageIndex - 1 }));
    }
  };

  handleSetPageSize = (e) => {
    const size = Number(e.target.value);
    if (this.props.manualPagination && this.props.onPageSizeChange) {
      this.props.onPageSizeChange(size);
    } else {
      this.setState({ pageSize: size, pageIndex: 0 });
    }
  };

  getPaginationMeta = (filteredSortedData) => {
    const { manualPagination = false, pageIndex: cpi, pageSize: cps, totalRowCount = 0 } = this.props;
    const pageIndex = manualPagination
      ? Math.max(0, cpi ?? 0)
      : this.state.pageIndex;
    const pageSize = manualPagination
      ? Math.max(1, cps ?? 10)
      : this.state.pageSize;
    const pageCount = manualPagination
      ? Math.max(1, Math.ceil((Number(totalRowCount) || 0) / pageSize))
      : Math.max(1, Math.ceil(filteredSortedData.length / pageSize) || 1);
    const pageData = manualPagination
      ? filteredSortedData
      : filteredSortedData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
    return { pageIndex, pageSize, pageCount, pageData };
  };

  handleMouseDown = (e, columnAccessor) => {
    e.preventDefault();
    this.setState({
      isResizing: true,
      resizingColumn: columnAccessor,
    });

    const startX = e.clientX;
    const startWidth = this.state.columnWidths[columnAccessor] || 150;

    const handleMouseMove = (e) => {
      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
      this.setState(prevState => ({
        columnWidths: {
          ...prevState.columnWidths,
          [columnAccessor]: newWidth,
        },
      }));
    };

    const handleMouseUp = () => {
      this.setState({
        isResizing: false,
        resizingColumn: null,
      });
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  getFilteredSortedData = () => {
    const { data, disableSort = false } = this.props;
    const { filters, sortBy } = this.state;

    let filteredData = data.filter(row => {
      return Object.entries(filters).every(([columnId, filterValue]) => {
        if (!filterValue) return true;
        const cellValue = String(row[columnId] || '').toLowerCase();
        return cellValue.includes(String(filterValue).toLowerCase());
      });
    });

    if (!disableSort && sortBy && sortBy.length > 0 && sortBy[0]) {
      const { id, desc } = sortBy[0];
      filteredData.sort((a, b) => {
        if (a[id] < b[id]) return desc ? 1 : -1;
        if (a[id] > b[id]) return desc ? -1 : 1;
        return 0;
      });
    }

    return filteredData;
  }

  renderCell = (column, row) => {
    if (column.Cell) {
      return column.Cell({ value: row[column.accessor], row: { original: row } });
    }
    return row[column.accessor];
  }

  render() {
    const {
      columns,
      tbodyBorder = '1px solid black',
      rowKeyAccessor,
      manualPagination = false,
      compactOnMobile = false,
      disableSort = false,
      dense = false,
      fontSize,
    } = this.props;
    const { sortBy, columnWidths, isResizing, resizingColumn } = this.state;
    const filteredSortedData = this.getFilteredSortedData();
    const { pageIndex, pageSize, pageCount, pageData } = this.getPaginationMeta(filteredSortedData);
    const tableClass = compactOnMobile ? styles.table : undefined;
    const wrapperClass = compactOnMobile ? styles.tableWrapper : undefined;
    const cellFontSize = fontSize || (dense ? '13px' : undefined);
    const cellPadding = dense ? '4px 6px' : undefined;

    return (
      <Box overflowX="auto" width="100%" className={wrapperClass}>
        <Table size="sm" variant="simple" ref={this.tableRef} className={tableClass} fontSize={cellFontSize}>
          <Thead>
            <Tr>
              {columns.map((column, index) => {
                const width = columnWidths[column.accessor] || column.defaultWidth || 150;
                const isLastColumn = index === columns.length - 1;
                const isCurrentResizing = resizingColumn === column.accessor;
                
                return (
                  <React.Fragment key={column.accessor || column.id || index}>
                    <Th 
                      onClick={disableSort ? undefined : () => this.handleSort(column.accessor)} 
                      border="1px solid gray.300"
                      width={`${width}px`}
                      minWidth={`${width}px`}
                      maxWidth={`${width}px`}
                      position="relative"
                      userSelect="none"
                      cursor={disableSort ? 'default' : 'pointer'}
                      px={dense ? '6px' : undefined}
                      py={dense ? '4px' : undefined}
                      fontSize={cellFontSize}
                      backgroundColor={isCurrentResizing ? "gray.100" : "inherit"}
                      _hover={disableSort ? undefined : { backgroundColor: "gray.50" }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box flex="1" overflow="hidden">
                          {column.Header}
                          {!disableSort && (
                            <chakra.span pl="1">
                              {sortBy[0] && sortBy[0].id === column.accessor && (
                                sortBy[0].desc ? <ArrowDownIcon aria-label="sorted descending" /> : <ArrowUpIcon aria-label="sorted ascending" />
                              )}
                            </chakra.span>
                          )}
                        </Box>
                        {!isLastColumn && !disableSort && (
                          <Box
                            width="4px"
                            height="100%"
                            position="absolute"
                            right="-2px"
                            top="0"
                            cursor="col-resize"
                            backgroundColor={isCurrentResizing ? "blue.400" : "transparent"}
                            _hover={{ backgroundColor: "blue.300" }}
                            zIndex="10"
                            onMouseDown={(e) => this.handleMouseDown(e, column.accessor)}
                            style={{
                              borderRight: isCurrentResizing ? "2px solid #3182ce" : "1px solid transparent"
                            }}
                          />
                        )}
                      </Box>
                      {this.props.showColumnFilters !== false && (
                        <div>
                          <Input
                            size="sm"
                            placeholder={`Filter ${column.Header}`}
                            value={this.state.filters[column.accessor] || ''}
                            onChange={(e) => this.handleFilter(column.accessor, e.target.value)}
                          />
                        </div>
                      )}
                    </Th>
                  </React.Fragment>
                );
              })}
            </Tr>
          </Thead>
          <Tbody border={tbodyBorder}>
            {pageData.map((row, rowIndex) => (
              <Tr
                key={rowKeyAccessor && row[rowKeyAccessor] != null ? String(row[rowKeyAccessor]) : rowIndex}
                style={this.props.getRowStyle ? this.props.getRowStyle(row) : {}}
                _hover={{
                  backgroundColor: '#d6d6c2',
                }}
              >
                {columns.map(column => {
                  const width = columnWidths[column.accessor] || column.defaultWidth || 150;
                  return (
                    <Td
                      key={column.accessor || column.id}
                      border="1px solid black"
                      width={`${width}px`}
                      minWidth={`${width}px`}
                      maxWidth={`${width}px`}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      px={dense ? '6px' : undefined}
                      py={dense ? '4px' : undefined}
                      fontSize={cellFontSize}
                      style={{
                        color: this.props.getRowStyle ? this.props.getRowStyle(row)?.color : undefined,
                        backgroundColor: this.props.getRowStyle ? this.props.getRowStyle(row)?.backgroundColor : undefined,
                        fontWeight: this.props.getRowStyle ? this.props.getRowStyle(row)?.fontWeight : undefined,
                        padding: cellPadding,
                      }}
                    >
                      {this.renderCell(column, row)}
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Flex
          justifyContent="space-between"
          m={4}
          alignItems="center"
          className={compactOnMobile ? styles.pagination : undefined}
          flexWrap="wrap"
          gap={2}
        >
          <Flex className={compactOnMobile ? styles.paginationNav : undefined}>
            <Tooltip label="First Page">
              <IconButton
                onClick={() => this.handleGotoPage(0)}
                isDisabled={pageIndex === 0}
                icon={<ChevronLeftIcon h={3} w={3} />}
                mr={4}
              />
            </Tooltip>
            <Tooltip label="Previous Page">
              <IconButton
                onClick={this.handlePreviousPage}
                isDisabled={pageIndex === 0}
                icon={<ChevronLeftIcon h={6} w={6} />}
              />
            </Tooltip>
          </Flex>

          <Flex alignItems="center" className={compactOnMobile ? styles.paginationControls : undefined}>
            <Text flexShrink="0" mr={8} className={compactOnMobile ? styles.paginationText : undefined}>
              Page{' '}
              <Text fontWeight="bold" as="span">
                {pageIndex + 1}
              </Text>{' '}
              of{' '}
              <Text fontWeight="bold" as="span">
                {pageCount}
              </Text>
            </Text>
            <Text flexShrink="0" className={compactOnMobile ? styles.paginationText : undefined}>Go to page:</Text>{' '}
            <Box className={compactOnMobile ? styles.paginationInput : undefined}>
            <NumberInput
              ml={2}
              mr={8}
              w={28}
              min={1}
              max={pageCount}
              value={String(pageIndex + 1)}
              onChange={(_, valueAsNumber) => {
                if (valueAsNumber === '' || Number.isNaN(Number(valueAsNumber))) return;
                const clamped = Math.min(Math.max(1, Number(valueAsNumber)), pageCount);
                this.handleGotoPage(clamped - 1);
              }}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            </Box>
            <Box className={compactOnMobile ? styles.paginationSelect : undefined}>
            <Select
              w={32}
              value={pageSize}
              onChange={this.handleSetPageSize}
            >
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            </Box>
          </Flex>

          <Flex className={compactOnMobile ? styles.paginationNav : undefined}>
            <Tooltip label="Next Page">
              <IconButton
                onClick={this.handleNextPage}
                isDisabled={pageIndex === pageCount - 1}
                icon={<ChevronRightIcon h={6} w={6} />}
              />
            </Tooltip>
            <Tooltip label="Last Page">
              <IconButton
                onClick={() => this.handleGotoPage(pageCount - 1)}
                isDisabled={pageIndex === pageCount - 1}
                icon={<ChevronRightIcon h={3} w={3} />}
                ml={4}
              />
            </Tooltip>
          </Flex>
        </Flex>
      </Box>
    );
  }
}

export default GenericTable;